import React, { useState, useEffect } from "react";
import { Copy, Check, ExternalLink, Plus, Loader2 } from "lucide-react";
import { useDispatch } from "react-redux";
import { submitTool } from "actions/tool";
import { crawlURL } from "actions/admin";
import { toast } from "sonner";
import axios from "axios";

const URLCrawler = ({ onCrawl }) => {
    const dispatch = useDispatch();
    const [url, setUrl] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState([]);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [submittingTools, setSubmittingTools] = useState({});
    const [crawlId, setCrawlId] = useState(null);
    const [crawlStatus, setCrawlStatus] = useState(null);

    // Poll for crawl results
    useEffect(() => {
        let pollInterval;

        if (crawlId && crawlStatus === "processing") {
            pollInterval = setInterval(async () => {
                try {
                    const response = await axios.get(
                        `/api/admin/crawl-results/${crawlId}`
                    );
                    const data = response.data;

                    if (
                        data.status === "completed" ||
                        data.status === "failed"
                    ) {
                        setCrawlStatus(data.status);
                        setResults(data.tools || []);
                        clearInterval(pollInterval);

                        if (data.status === "failed") {
                            toast.error(data.errorMessage || "Crawl failed");
                        } else if (data.hasError) {
                            toast.warning("Crawl completed with some errors");
                        } else {
                            toast.success(
                                `Found ${data.totalToolsFound} tools`
                            );
                        }
                    } else {
                        // Update results even while processing
                        setResults(data.tools || []);
                    }
                } catch (error) {
                    console.error("Error polling crawl results:", error);
                    clearInterval(pollInterval);
                    setCrawlStatus("failed");
                    toast.error("Failed to fetch crawl results");
                }
            }, 2000); // Poll every 2 seconds
        }

        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [crawlId, crawlStatus]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!url) {
            setError("Please enter a URL");
            return;
        }

        setIsLoading(true);
        setCrawlStatus("processing");
        setResults([]);
        setCrawlId(null);

        try {
            const response = await dispatch(crawlURL(url));
            setCrawlId(response.crawlId);
            toast.info("Crawl started. Results will appear as they are found.");
        } catch (error) {
            console.error("Error starting crawl:", error);
            setCrawlStatus("failed");
            toast.error(error.message || "Failed to start crawl");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyUrl = async (url, index) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch (err) {
            console.error("Failed to copy URL:", err);
        }
    };

    const handleOpenUrl = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleSubmitTool = async (tool) => {
        try {
            setSubmittingTools((prev) => ({ ...prev, [tool.url]: true }));

            // Prepare tool data for submission
            const toolData = {
                name: tool.name,
                externalUrl: tool.url,
            };

            await dispatch(submitTool(toolData));

            // Remove from submitting state
            setSubmittingTools((prev) => {
                const newState = { ...prev };
                delete newState[tool.url];
                return newState;
            });
        } catch (error) {
            console.error("Error submitting tool:", error);
            // Remove from submitting state even if there's an error
            setSubmittingTools((prev) => {
                const newState = { ...prev };
                delete newState[tool.url];
                return newState;
            });
        }
    };

    return (
        <div className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label
                        htmlFor="url"
                        className="block text-sm font-medium text-gray-700"
                    >
                        Enter Toolify.ai Category URL to crawl
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                        <input
                            type="url"
                            name="url"
                            id="url"
                            className="flex-1 min-w-0 block w-full px-3 py-2 rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="https://example.com"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                        />
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                        >
                            {isLoading ? (
                                <span className="flex items-center">
                                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                                    Crawling...
                                </span>
                            ) : (
                                "Crawl URL"
                            )}
                        </button>
                    </div>
                </div>
            </form>

            {error && (
                <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                </div>
            )}

            {crawlStatus === "processing" && (
                <div className="text-sm text-gray-600">
                    Crawling in progress... Found {results.length} tools so far
                </div>
            )}

            {results && results.length > 0 && (
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900">
                            Crawled Tools ({results.length})
                        </h3>
                    </div>
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                        <ul className="divide-y divide-gray-200">
                            {results.map((tool, index) => (
                                <li
                                    key={index}
                                    className="px-6 py-4 hover:bg-gray-50"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {tool.name || "Unnamed Tool"}
                                            </p>
                                            <p className="text-sm text-gray-500 truncate">
                                                {tool.url || "No URL available"}
                                            </p>
                                        </div>
                                        <div className="ml-4 flex-shrink-0 flex space-x-2">
                                            <button
                                                onClick={() =>
                                                    handleCopyUrl(
                                                        tool.url,
                                                        index
                                                    )
                                                }
                                                className="inline-flex items-center p-1.5 border border-gray-300 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                title="Copy URL"
                                            >
                                                {copiedIndex === index ? (
                                                    <Check className="h-4 w-4 text-green-500" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleOpenUrl(tool.url)
                                                }
                                                className="inline-flex items-center p-1.5 border border-gray-300 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                title="Open URL"
                                            >
                                                <ExternalLink className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleSubmitTool(tool)
                                                }
                                                disabled={
                                                    submittingTools[tool.url]
                                                }
                                                className="inline-flex items-center p-1.5 border border-transparent rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Submit Tool"
                                            >
                                                {submittingTools[tool.url] ? (
                                                    <Loader2 className="animate-spin h-4 w-4 text-white" />
                                                ) : (
                                                    <Plus className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {crawlStatus === "failed" && results.length === 0 && (
                <div className="text-red-600">
                    No tools were found. Please check the URL and try again.
                </div>
            )}
        </div>
    );
};

export default URLCrawler;
